/** Matches `BundleDemandExplosionView.tsx` exception filter options. */
export const EXCEPTION_FILTER_OPTIONS = [
  { value: 'all', label: 'All rows' },
  { value: 'exceptions', label: 'All exceptions' },
  { value: 'blank-component', label: 'Blank component outputs' },
  { value: 'missing-component-record', label: 'Unmapped component SKUs' },
  { value: 'missing-execution-record', label: 'Unmapped execution SKUs' },
];

export function formatBundleExplosionNumber(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value || 0);
}

export function formatBundleExplosionUnits(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value || 0);
}

export function sanitizeBundleExplosionFilePart(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'bundle-demand-exceptions';
}

export function downloadBundleExplosionCsv(filenameRoot, headers, rows) {
  const csv = [headers, ...rows]
    .map(row => row.map(value => `"${String(value).replaceAll('"', '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeBundleExplosionFilePart(filenameRoot)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Matches example `getStatusLabel`. */
export function getBundleExplosionStatusLabel(row) {
  if (row.blankComponentSku) return 'Blank workbook output';
  if (row.missingExecution) return 'Execution SKU not visible';
  if (row.missingComponent) return 'Component SKU not visible';
  return 'Lineage mapped';
}

/** Matches example `matchesExceptionFilter`. */
export function matchesBundleExplosionExceptionFilter(row, exceptionFilter) {
  if (exceptionFilter === 'all') return true;
  if (exceptionFilter === 'exceptions') return row.hasException;
  if (exceptionFilter === 'blank-component') return row.blankComponentSku;
  if (exceptionFilter === 'missing-component-record') return !row.blankComponentSku && row.missingComponent;
  if (exceptionFilter === 'missing-execution-record') return row.missingExecution;
  return true;
}

export function filterBundleExplosionRows(rows, { search = '', exceptionFilter = 'all' } = {}) {
  const normalizedSearch = search.trim().toLowerCase();
  return (rows || []).filter(row => {
    const matchesSearch =
      !normalizedSearch ||
      row.executionSku.toLowerCase().includes(normalizedSearch) ||
      row.executionProductName.toLowerCase().includes(normalizedSearch) ||
      row.componentSku.toLowerCase().includes(normalizedSearch) ||
      row.componentProductName.toLowerCase().includes(normalizedSearch) ||
      row.supplier.toLowerCase().includes(normalizedSearch);

    return matchesSearch && matchesBundleExplosionExceptionFilter(row, exceptionFilter);
  });
}

/** Matches example summary `unmappedComponentCount` logic. */
export function summarizeBundleExplosionRows(filteredRows) {
  const executionBundles = new Set(filteredRows.map(row => row.executionSku));
  const components = new Set(filteredRows.map(row => row.componentSku));
  const exceptionCount = filteredRows.filter(row => row.hasException).length;
  const blankOutputCount = filteredRows.filter(row => row.blankComponentSku).length;
  const unmappedComponentCount = filteredRows.filter(
    row => !row.blankComponentSku && row.missingComponent
  ).length;
  const totalComponentUnits = filteredRows.reduce((sum, row) => sum + row.componentUnits, 0);
  /** Bundle-level forecast units: once per execution SKU, not per exploded component row. */
  let totalForecastUnits = 0;
  const forecastUnitsByExecution = new Map();
  for (const row of filteredRows) {
    if (forecastUnitsByExecution.has(row.executionSku)) continue;
    forecastUnitsByExecution.set(row.executionSku, row.forecastUnits);
    totalForecastUnits += row.forecastUnits;
  }

  return {
    bundleCount: executionBundles.size,
    componentCount: components.size,
    totalComponentUnits,
    totalForecastUnits,
    exceptionCount,
    blankOutputCount,
    unmappedComponentCount,
  };
}

export function buildBundleExplosionGroups(filteredRows, limit = 8) {
  const grouped = new Map();
  filteredRows.forEach(row => {
    const current = grouped.get(row.executionSku);
    if (current) {
      current.componentCount += 1;
      current.componentUnits += row.componentUnits;
      current.exceptionCount += row.hasException ? 1 : 0;
      return;
    }
    grouped.set(row.executionSku, {
      executionSku: row.executionSku,
      executionProductName: row.executionProductName,
      componentCount: 1,
      componentUnits: row.componentUnits,
      exceptionCount: row.hasException ? 1 : 0,
    });
  });
  return Array.from(grouped.values())
    .sort(
      (left, right) =>
        right.componentUnits - left.componentUnits ||
        left.executionSku.localeCompare(right.executionSku)
    )
    .slice(0, limit);
}

/** Matches example CSV resolution guidance. */
export function getBundleExplosionExportGuidance(row) {
  if (row.blankComponentSku) {
    return 'Review workbook formula or bundle BOM mapping for the blank component output.';
  }
  if (row.missingExecution) {
    return 'Confirm the execution bundle SKU exists in the visible demand planning scope.';
  }
  return 'Confirm the component SKU exists in the visible demand planning scope or seed data.';
}

/** Matches example inspect drawer audit guidance. */
export function getBundleExplosionAuditGuidance(row) {
  if (!row) return '';
  if (row.blankComponentSku) {
    return 'The workbook produced a blank component output. This usually means the formula chain or bundle BOM mapping needs review before downstream demand is trusted.';
  }
  if (row.missingExecution) {
    return 'The execution bundle exists in the workbook output but is not visible in the current demand-planning record set. Confirm the execution SKU is seeded and in scope.';
  }
  if (row.missingComponent) {
    return 'The component SKU appears in the workbook output but is not visible in the current demand-planning record set. Confirm the component SKU exists and is mapped correctly.';
  }
  return 'This bundle row is fully mapped. Use the lineage table to verify units-per-bundle and component demand against the workbook logic.';
}
